import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { COMPANY } from "../data/content";
import { api, formatApiError } from "../api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", hp: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type: "contact" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-32 pb-24" data-testid="contact-page">
      <Seo title="Contact" description="Contact Trendsight Media Private Limited, Delhi. Call +91 73018 80107 or email us to start a conversation about your brand's growth." />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="font-mono-accent text-xs uppercase tracking-[0.35em] text-blue-400 mb-5">
          Contact
        </motion.p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-50 leading-[1.02]" data-testid="contact-headline">
          LET'S START A <span className="text-gradient">CONVERSATION.</span>
        </h1>

        <div className="mt-14 grid lg:grid-cols-[1fr_1.1fr] gap-10">
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-7" data-testid="contact-details-card">
              <h2 className="font-display text-lg font-bold text-slate-50">{COMPANY.name}</h2>
              <p className="mt-4 flex items-start gap-3 text-sm text-slate-400 leading-relaxed">
                <MapPin size={16} className="text-blue-400 mt-0.5 shrink-0" />
                KH. NO. 37/6, 1ST FLOOR, STREET NO. 13/2, NEW-12 BLK-B, KAUSHIK ENCLAVE, LANDMARK NEAR GOVT HOSPITAL, DELHI – 110084
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-widest text-slate-500">GSTIN</p>
                  <p className="text-slate-300 mt-1">{COMPANY.gstin}</p>
                </div>
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-widest text-slate-500">CIN</p>
                  <p className="text-slate-300 mt-1">{COMPANY.cin}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <a href={`tel:+91${COMPANY.phone}`} data-testid="contact-call-btn" className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-6 hover:border-blue-500/50 transition-all duration-300">
                <Phone size={20} className="text-blue-400" />
                <p className="mt-3 text-sm font-semibold text-slate-200 group-hover:text-blue-300 transition-colors">Call us</p>
                <p className="text-[13px] text-slate-500 mt-1">{COMPANY.phoneDisplay}</p>
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-btn" className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-6 hover:border-blue-500/50 transition-all duration-300">
                <Mail size={20} className="text-blue-400" />
                <p className="mt-3 text-sm font-semibold text-slate-200 group-hover:text-blue-300 transition-colors">Email us</p>
                <p className="text-[13px] text-slate-500 mt-1 break-all">{COMPANY.email}</p>
              </a>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 h-56 relative overflow-hidden flex items-center justify-center bg-grid" data-testid="contact-map-placeholder">
              <div className="text-center">
                <MapPin size={30} className="text-blue-400 mx-auto animate-bounce" />
                <p className="mt-3 text-sm text-slate-400">Kaushik Enclave, Delhi – 110084</p>
                <p className="mt-1 font-mono-accent text-[10px] uppercase tracking-widest text-slate-600">Google Maps integration placeholder</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 sm:p-10" data-testid="contact-form-card">
            {done ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12" data-testid="contact-success">
                <CheckCircle2 size={44} className="text-emerald-400" />
                <h2 className="mt-5 font-display text-2xl font-bold text-slate-50">Thank you.</h2>
                <p className="mt-3 text-slate-400 max-w-md leading-relaxed">
                  Thank you for contacting TRENDSIGHT MEDIA. Your enquiry has been received successfully. Our team will review your requirement and contact you shortly.
                </p>
                <a
                  href={`https://wa.me/91${COMPANY.phone}?text=${encodeURIComponent("Hi Trendsight Media, I just submitted an enquiry on your website.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-7 py-3.5 transition-all duration-300 active:scale-95"
                >
                  <MessageCircle size={17} /> Chat with us on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <h2 className="font-display text-xl font-bold text-slate-50">Send us a message</h2>
                <input type="text" name="hp" value={form.hp} onChange={set("hp")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-2" htmlFor="c-name">Name *</label>
                    <input id="c-name" required value={form.name} onChange={set("name")} data-testid="contact-name-input" className="w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-2" htmlFor="c-phone">Phone *</label>
                    <input id="c-phone" required value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className="w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-2" htmlFor="c-email">Email *</label>
                  <input id="c-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" className="w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-2" htmlFor="c-company">Company Name</label>
                  <input id="c-company" value={form.company} onChange={set("company")} data-testid="contact-company-input" className="w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-2" htmlFor="c-message">Message *</label>
                  <textarea id="c-message" required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" className="w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors resize-none" />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold px-8 py-4 transition-all duration-300 active:scale-[0.98]"
                >
                  {sending ? "Sending..." : "Send Message"}
                  <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
